import * as log from '@sb/log';
import type { Pose } from '@sb/messenger/auto_generated/messenger/geometry';
import {
  TCErrorCode,
  type EnqueueReply,
  type Primitive,
  type SafetySession,
} from '@sb/messenger/auto_generated/messenger/trajectory_controller_service';
import type { TrajectoryControllerConnector } from '@sb/messenger/connectors/generated';
import { wait } from '@sb/utilities';

const ns = log.namespace('enqueueWithTcPrerequisites');

// SetSafetySession applies on the TC's next 1 kHz tick; wait a few ticks before
// the retry so it observes the applied session. The WorldToBaseUpdate enqueue
// reply resolves once the RT thread has queued it, and it applies at
// activation, so the same settle covers it. Only paid on the recovery path.
const PREREQUISITE_SETTLE_MS = 20;

export interface EnqueueWithTcPrerequisitesOptions {
  enqueueTimeoutMs: number;
  setSafetySessionTimeoutMs: number;
  /** Override the post-push settle delay (tests pass 0). */
  settleMs?: number;
  /** Abort the push/retry recovery if the caller cancelled mid-flight. */
  isCancelled?: () => boolean;
}

/**
 * Where the recovery path reads the current TC prerequisites from.
 * `RoutineContext` satisfies this structurally.
 */
export interface TcPrerequisiteSource {
  getTcSafetySession(): SafetySession | null;
  getTcWorldToBase(): Pose;
}

/**
 * Enqueue a TC primitive, recovering from missing TC prerequisites on
 * `TC_ERROR_NOT_READY` (e.g. after a TC restart). The wire error does not say
 * which prerequisite was missing, so the recovery pushes the current safety
 * session, then the current world→base pose, then retries the Enqueue once.
 * Each push must succeed before the next step runs; any failure surfaces the
 * original rejection. Both prerequisites are read lazily from `source` at
 * recovery time.
 */
export async function enqueueWithTcPrerequisites(
  connector: TrajectoryControllerConnector,
  primitive: Primitive,
  source: TcPrerequisiteSource,
  options: EnqueueWithTcPrerequisitesOptions,
): Promise<EnqueueReply> {
  const reply = await connector.Enqueue(primitive, options.enqueueTimeoutMs);

  if (reply.accepted || reply.error !== TCErrorCode.TC_ERROR_NOT_READY) {
    return reply;
  }

  if (options.isCancelled?.()) {
    return reply;
  }

  // Resolved lazily: the session may have synced while the first Enqueue awaited.
  const safetySession = source.getTcSafetySession();

  if (!safetySession) {
    log.warn(ns`recover`, 'NOT_READY but no safety session available to push');

    return reply;
  }

  log.info(
    ns`recover`,
    'NOT_READY; pushing safety session + world_to_base and retrying once',
  );

  try {
    await connector.SetSafetySession(
      safetySession,
      options.setSafetySessionTimeoutMs,
    );
  } catch (error) {
    log.warn(ns`recover`, 'SetSafetySession during recovery failed', { error });

    return reply;
  }

  try {
    const worldToBaseReply = await connector.Enqueue(
      { worldToBaseUpdate: { worldToBase: source.getTcWorldToBase() } },
      options.enqueueTimeoutMs,
    );

    if (!worldToBaseReply.accepted) {
      log.warn(ns`recover`, 'WorldToBaseUpdate during recovery rejected', {
        error: worldToBaseReply.error,
      });

      return reply;
    }
  } catch (error) {
    log.warn(ns`recover`, 'WorldToBaseUpdate during recovery failed', {
      error,
    });

    return reply;
  }

  await wait(options.settleMs ?? PREREQUISITE_SETTLE_MS);

  // Re-check: the operator may have released during the push/settle window.
  if (options.isCancelled?.()) {
    return reply;
  }

  return connector.Enqueue(primitive, options.enqueueTimeoutMs);
}
